/**
 * ShareDialog - Project sharing configuration modal
 * Allows photographer to create/update a public share link for a project.
 * Clients access via app.masterlapse.com/s/{shareToken}
 */

import { useState, useEffect, useRef } from "react";
import {
  Modal,
  Switch,
  Select,
  Checkbox,
  Button,
  Upload,
  Tag,
  Spin,
  Tooltip,
  TimePicker,
  InputNumber,
  message,
  Typography,
  Grid,
} from "antd";
import dayjs from "dayjs";
import { MaterialIcon } from "@/components/MaterialIcon";
import { useAuthStore } from "@/store/authStore";
import { getCurrentUserProfile } from "@/services/userService";
import {
  createShare,
  getShareByProject,
  updateShare,
  uploadLogo,
  deleteLogo,
} from "@/services/shareService";
import type {
  Project,
  View,
  ProjectShare,
  ShareTimespan,
  VideoFPS,
  VideoAspectRatio,
  WhatsAppDailyConfig,
} from "@/types";
import { MODAL_BLUR_STYLES } from "@/styles/modalStyles";
import {
  SECTION_GAP,
  RADIUS,
  BORDER,
  SECONDARY,
} from "@/styles/design-tokens";
import { SectionLabel } from "@/components/SectionLabel";
import { FieldLabel } from "@/components/FieldLabel";
import { copyToClipboard } from "@/utils/clipboard";
import WhatsAppRecipients from "@/components/WhatsAppRecipients";
import { useViewEntitlements } from "@/hooks/useViewEntitlements";
import { GatedControl } from "@/components/TierBadge";

const COMMON_TIMEZONES = [
  "Europe/Rome", "Europe/Berlin", "Europe/Paris", "Europe/Madrid",
  "Europe/London", "Europe/Amsterdam", "Europe/Zurich", "Europe/Vienna",
  "Europe/Brussels", "Europe/Stockholm", "Europe/Oslo", "Europe/Copenhagen",
  "Europe/Helsinki", "Europe/Warsaw", "Europe/Prague", "Europe/Budapest",
  "Europe/Athens", "Europe/Istanbul", "Europe/Moscow",
  "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles",
  "America/Toronto", "America/Sao_Paulo", "America/Mexico_City",
  "Asia/Tokyo", "Asia/Shanghai", "Asia/Hong_Kong", "Asia/Singapore",
  "Asia/Dubai", "Asia/Kolkata", "Asia/Seoul",
  "Australia/Sydney", "Australia/Melbourne", "Pacific/Auckland",
  "Africa/Cairo", "Africa/Johannesburg", "Africa/Nairobi",
];

const { Text } = Typography;

const ALL_TIMESPANS: { value: ShareTimespan; label: string }[] = [
  { value: "1h", label: "1 hour" },
  { value: "1d", label: "1 day" },
  { value: "3d", label: "3 days" },
  { value: "1w", label: "1 week" },
  { value: "2w", label: "2 weeks" },
  { value: "1m", label: "1 month" },
];

const FPS_OPTIONS: VideoFPS[] = [15, 24, 25, 30, 60];
const ASPECT_RATIO_OPTIONS: VideoAspectRatio[] = ["16:9", "9:16", "4:3", "3:4", "1:1"];

interface ShareDialogProps {
  open: boolean;
  project: Project;
  views: View[];
  onClose: () => void;
}

export default function ShareDialog({
  open,
  project,
  views,
  onClose,
}: ShareDialogProps) {
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.sm;
  const { user } = useAuthStore();

  // Loading state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Auto-save plumbing: skip the save right after initial load, debounce changes
  const skipAutoSaveRef = useRef(true);
  const autoSaveTimerRef = useRef<number | null>(null);
  // Holds the latest persistChanges closure so onClose can flush a pending save
  // without waiting for the 600ms debounce.
  const pendingPersistRef = useRef<(() => Promise<void>) | null>(null);

  // Tracks where the mouse was pressed on the public link, to distinguish
  // a click (= copy) from a drag (= text selection).
  const linkClickStartRef = useRef<{ x: number; y: number } | null>(null);

  // Share data
  const [share, setShare] = useState<ProjectShare | null>(null);

  // Form state
  const [enabled, setEnabled] = useState(true);
  const [galleryEnabled, setGalleryEnabled] = useState(true);
  const [videoEnabled, setVideoEnabled] = useState(true);
  const [resolution, setResolution] = useState<"1080p" | "4k">("1080p");
  const [fps, setFps] = useState<VideoFPS>(24);
  const [aspectRatio, setAspectRatio] = useState<VideoAspectRatio>("16:9");
  const [allowedTimespans, setAllowedTimespans] = useState<ShareTimespan[]>([
    "1d", "1w", "1m",
  ]);
  const [excludedViewIds, setExcludedViewIds] = useState<string[]>([]);
  const [viewOrder, setViewOrder] = useState<string[]>([]);
  const [draggedViewId, setDraggedViewId] = useState<string | null>(null);
  const [dragOverViewId, setDragOverViewId] = useState<string | null>(null);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  // WhatsApp daily state
  const [waEnabled, setWaEnabled] = useState(false);
  const [waRecipients, setWaRecipients] = useState<string[]>([]);
  const [waSendTime, setWaSendTime] = useState("18:00");
  const [waIntervalDays, setWaIntervalDays] = useState(1);
  const [waTimezone, setWaTimezone] = useState("");
  const [waTimezoneSource, setWaTimezoneSource] = useState<"view" | "manual">("view");
  const [waOptedOut, setWaOptedOut] = useState<string[]>([]);
  // Legacy V2 accounts have no view license: only "pro" ever granted sharing.
  const [legacyAllowsSharing, setLegacyAllowsSharing] = useState(false);

  const [photographerName, setPhotographerName] = useState("");

  // Only the views actually on the share count — the ones left after
  // excludedViewIds. A Plus view excluded from the share must not unlock
  // sharing for the free views that stayed on it.
  // Mirrors enforceShareEntitlement server-side.
  const { get: getEntitlements } = useViewEntitlements();
  const sharedEntitlements = views
    .filter((v) => !excludedViewIds.includes(v.viewId))
    .map((v) => getEntitlements(v.viewId));
  const anyLicensed = sharedEntitlements.some((e) => e.licensed);
  const sharingAllowed = anyLicensed
    ? sharedEntitlements.some((e) => e.sharing)
    : legacyAllowsSharing;
  const maxResolution: "1080p" | "4k" = anyLicensed
    ? sharedEntitlements.some((e) => e.maxResolution === "4k")
      ? "4k"
      : "1080p"
    : legacyAllowsSharing
      ? "4k"
      : "1080p";
  // The tier to name on the badge when sharing is locked.
  const sharingTier = "plus" as const;

  // Load existing share + user profile when dialog opens
  useEffect(() => {
    if (!open || !user) return;

    let cancelled = false;

    async function loadData() {
      setLoading(true);
      skipAutoSaveRef.current = true;
      try {
        const [existingShare, profile] = await Promise.all([
          getShareByProject(user!.uid, project.projectId),
          getCurrentUserProfile(),
        ]);

        if (cancelled) return;

        setPhotographerName(profile?.name || user!.displayName || "");

        // Resolution and sharing come from the licenses of the views in this
        // project, resolved by useViewEntitlements below. A share covers a
        // whole project, so one entitled view is enough — the same rule the
        // enforceShareEntitlement trigger applies server-side.
        //
        // This used to be a hardcoded `pro || beta` whitelist written twice in
        // consecutive lines, reading neither the license nor config/plans_v3.
        const legacyTier = profile?.plan?.tier || "free";
        setLegacyAllowsSharing(legacyTier === "pro");

        // Detect timezone from first shared view
        const viewTimezone = views.find((v) => v.androidStatus?.device?.timezone)
          ?.androidStatus?.device?.timezone;

        if (existingShare) {
          setShare(existingShare);
          setEnabled(existingShare.enabled);
          setGalleryEnabled(existingShare.galleryEnabled ?? true);
          setVideoEnabled(existingShare.videoEnabled ?? true);
          setResolution(existingShare.videoSettings.resolution);
          setFps(existingShare.videoSettings.fps);
          setAspectRatio(existingShare.videoSettings.aspectRatio);
          const resolved =
            (existingShare.allowedTimespans && existingShare.allowedTimespans.length > 0
              ? existingShare.allowedTimespans
              : existingShare.timespan
                ? [existingShare.timespan]
                : ["1d", "1w", "1m"]) as ShareTimespan[];
          setAllowedTimespans(resolved);
          setExcludedViewIds(existingShare.excludedViewIds);
          setLogoUrl(existingShare.logoUrl);
          // WhatsApp config
          const wa = existingShare.whatsapp;
          if (wa) {
            setWaEnabled(wa.enabled);
            setWaRecipients(wa.recipients || []);
            setWaSendTime(wa.sendTime || "18:00");
            setWaIntervalDays(wa.intervalDays || 1);
            setWaTimezone(wa.timezone || viewTimezone || "");
            setWaTimezoneSource(wa.timezoneSource || "view");
            setWaOptedOut(wa.optedOut || []);
          } else {
            setWaEnabled(false);
            setWaRecipients([]);
            setWaSendTime("18:00");
            setWaIntervalDays(1);
            setWaTimezone(viewTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone);
            setWaTimezoneSource(viewTimezone ? "view" : "manual");
            setWaOptedOut([]);
          }
          // Merge persisted order with current views: known IDs first
          // (in saved order), then any new view appended in natural order.
          const savedOrder = existingShare.viewOrder ?? [];
          const known = savedOrder.filter((id) =>
            views.some((v) => v.viewId === id)
          );
          const missing = views
            .map((v) => v.viewId)
            .filter((id) => !known.includes(id));
          setViewOrder([...known, ...missing]);
        } else {
          // Defaults for new share
          setShare(null);
          setEnabled(true);
          setGalleryEnabled(true);
          setVideoEnabled(true);
          setResolution("1080p");
          setFps(24);
          setAspectRatio("16:9");
          setAllowedTimespans(["1d", "1w", "1m"]);
          setExcludedViewIds([]);
          setViewOrder(views.map((v) => v.viewId));
          setLogoUrl(null);
          // WhatsApp defaults for new share
          setWaEnabled(false);
          setWaRecipients([]);
          setWaSendTime("18:00");
          setWaIntervalDays(1);
          setWaTimezone(viewTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone);
          setWaTimezoneSource(viewTimezone ? "view" : "manual");
          setWaOptedOut([]);
        }
      } catch (error) {
        console.error("Failed to load share data:", error);
        message.error("Failed to load sharing settings");
      } finally {
        if (!cancelled) {
          setLoading(false);
          // Release the auto-save guard on the next tick so the state-derived
          // effect runs first with skip=true and only subsequent user edits
          // trigger a save.
          setTimeout(() => {
            if (!cancelled) skipAutoSaveRef.current = false;
          }, 0);
        }
      }
    }

    loadData();
    return () => { cancelled = true; };
    // Intentionally exclude `views` from deps: the parent re-creates the
    // views array reference on every Firestore snapshot, which would
    // re-run loadData and flash the loading spinner. We only need to
    // reload on dialog open / user / project change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, user, project.projectId]);

  // Persist current settings — used by the debounced auto-save effect
  const persistChanges = async () => {
    if (!user) return;

    setSaving(true);
    try {
      const whatsappConfig: WhatsAppDailyConfig | undefined = sharingAllowed ? {
        enabled: waEnabled,
        recipients: waRecipients,
        sendTime: waSendTime,
        intervalDays: waIntervalDays,
        timezone: waTimezone,
        timezoneSource: waTimezoneSource,
        optedOut: waOptedOut,
        lastSent: share?.whatsapp?.lastSent || {},
      } : undefined;

      if (share) {
        await updateShare(share.shareId, {
          enabled,
          projectName: project.name,
          photographerName,
          logoUrl,
          videoSettings: { resolution, fps, aspectRatio },
          allowedTimespans,
          excludedViewIds,
          viewOrder,
          galleryEnabled,
          videoEnabled,
          ...(whatsappConfig !== undefined ? { whatsapp: whatsappConfig } : {}),
        });
      } else {
        const newShare = await createShare(
          user.uid,
          project.projectId,
          project.name,
          photographerName,
          {
            videoSettings: { resolution, fps, aspectRatio },
            allowedTimespans,
            excludedViewIds,
            viewOrder,
            galleryEnabled,
            videoEnabled,
          }
        );
        setShare(newShare);
        // Auto-copy the public URL to clipboard the very first time the
        // share is created.
        const url = `${window.location.origin}/s/${newShare.shareToken}`;
        const copied = await copyToClipboard(url);
        message.success(
          copied ? "Link created and copied to clipboard" : "Share link created"
        );
      }
    } catch (error) {
      console.error("Failed to save share:", error);
      message.error("Failed to save sharing settings");
    } finally {
      setSaving(false);
    }
  };

  // Debounced auto-save — fires on any settings change, after 600ms of quiet
  useEffect(() => {
    if (!open || !user) return;
    if (loading) return;
    if (skipAutoSaveRef.current) return;

    if (autoSaveTimerRef.current !== null) {
      window.clearTimeout(autoSaveTimerRef.current);
    }
    // Remember the latest closure so a manual flush from onClose persists the
    // most recent state, not a stale one.
    pendingPersistRef.current = persistChanges;
    autoSaveTimerRef.current = window.setTimeout(() => {
      pendingPersistRef.current = null;
      persistChanges();
    }, 600);

    return () => {
      if (autoSaveTimerRef.current !== null) {
        window.clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    open,
    user,
    loading,
    enabled,
    galleryEnabled,
    videoEnabled,
    resolution,
    fps,
    aspectRatio,
    allowedTimespans,
    excludedViewIds,
    viewOrder,
    logoUrl,
    waEnabled,
    waRecipients,
    waSendTime,
    waIntervalDays,
    waTimezone,
  ]);

// Flush any pending debounced save, then call the parent's onClose.
  // Without this, dismissing the dialog within 600ms of the last change would
  // drop the edit (timer cleared by unmount before it fires).
  const handleClose = async () => {
    if (autoSaveTimerRef.current !== null) {
      window.clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }
    const pending = pendingPersistRef.current;
    pendingPersistRef.current = null;
    if (pending) {
      try {
        await pending();
      } catch {
        // persistChanges already shows an error toast
      }
    }
    onClose();
  };

  const handleCopyLink = async () => {
    if (!share) return;
    const url = `${window.location.origin}/s/${share.shareToken}`;
    if (await copyToClipboard(url)) {
      message.success("Link copied to clipboard");
    } else {
      message.error("Failed to copy link");
    }
  };

  const handleLogoUpload = async (file: File) => {
    if (!user) return;
    if (file.size > 2 * 1024 * 1024) {
      message.error("Logo must be smaller than 2 MB");
      return;
    }
    setUploadingLogo(true);
    try {
      const url = await uploadLogo(user.uid, file);
      setLogoUrl(url);
      message.success("Logo uploaded");
    } catch (error) {
      console.error("Failed to upload logo:", error);
      const code =
        error instanceof Error && "code" in error
          ? (error as { code: string }).code
          : null;
      const detail =
        code === "storage/unauthorized"
          ? "Not allowed — make sure Storage rules are deployed."
          : error instanceof Error
            ? error.message
            : "Unknown error";
      message.error(`Logo upload failed: ${detail}`);
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleLogoRemove = async () => {
    if (!user) return;
    try {
      await deleteLogo(user.uid);
      setLogoUrl(null);
      message.success("Logo removed");
    } catch (error) {
      console.error("Failed to remove logo:", error);
      message.error("Failed to remove logo");
    }
  };

  const toggleViewExclusion = (viewId: string) => {
    setExcludedViewIds((prev) => {
      const isExcluding = !prev.includes(viewId);
      // Block excluding the last shared view — at least one must stay shared
      if (isExcluding && views.length - prev.length <= 1) {
        message.warning("At least one view must be shared");
        return prev;
      }
      return prev.includes(viewId)
        ? prev.filter((id) => id !== viewId)
        : [...prev, viewId];
    });
  };

  const sharedCount = views.length - excludedViewIds.length;
  const allViewsShared = excludedViewIds.length === 0;

  // Views displayed in saved order; views not yet in viewOrder appear at the end.
  const orderedViews = (() => {
    const indexOf = new Map(viewOrder.map((id, i) => [id, i]));
    return [...views].sort((a, b) => {
      const ai = indexOf.has(a.viewId) ? (indexOf.get(a.viewId) as number) : Number.MAX_SAFE_INTEGER;
      const bi = indexOf.has(b.viewId) ? (indexOf.get(b.viewId) as number) : Number.MAX_SAFE_INTEGER;
      return ai - bi;
    });
  })();

  const reorderViews = (sourceId: string, targetId: string) => {
    if (sourceId === targetId) return;
    setViewOrder((prev) => {
      // Make sure every viewId is represented in the working list.
      const ids = [...prev];
      views.forEach((v) => {
        if (!ids.includes(v.viewId)) ids.push(v.viewId);
      });
      const from = ids.indexOf(sourceId);
      const to = ids.indexOf(targetId);
      if (from === -1 || to === -1) return prev;
      const next = [...ids];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  };

  return (
    <Modal
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: RADIUS,
              background: "#f0f5ff",
              color: "#1677ff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <MaterialIcon name="share" size={18} />
          </div>
          <span style={{ fontSize: 18, fontWeight: 600, letterSpacing: -0.2 }}>
            Share Project
          </span>
        </div>
      }
      open={open}
      onCancel={handleClose}
      width={isMobile ? '100vw' : 600}
      style={isMobile ? { top: 0, paddingBottom: 0, maxWidth: '100vw', margin: 0 } : undefined}
      styles={{
        ...MODAL_BLUR_STYLES,
        body: isMobile
          ? {
              overflowY: 'auto',
              maxHeight: 'calc(100vh - 160px)',
              paddingRight: 12,
            }
          : {
              overflowY: 'auto',
              maxHeight: 'calc(90vh - 160px)',
              paddingRight: 12,
            },
        ...(isMobile
          ? { content: { height: '100vh', borderRadius: 0 } }
          : {}),
      }}
      footer={
        loading ? null : (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 12,
              paddingTop: 8,
            }}
          >
            <span style={{ fontSize: 12, color: SECONDARY }}>
              {saving ? "Saving…" : "Changes saved automatically"}
            </span>
            <Button type="primary" onClick={handleClose}>
              Close
            </Button>
          </div>
        )
      }
    >
      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 40 }}>
          <Spin />
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: SECTION_GAP }}>
          {/* Enable/Disable toggle — pill-style block */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "12px 14px",
              border: BORDER,
              borderRadius: RADIUS,
              background: enabled ? "#f6ffed" : "#fafafa",
              transition: "background 180ms ease",
            }}
          >
            <div>
              <div style={{ fontSize: 15, fontWeight: 600 }}>
                {!sharingAllowed
                  ? "Sharing"
                  : enabled
                    ? "Sharing enabled"
                    : "Sharing disabled"}
              </div>
              <div style={{ fontSize: 12, color: SECONDARY, marginTop: 2 }}>
                {!sharingAllowed
                  ? "Give your client a link to browse photos, watch the player and receive WhatsApp updates."
                  : enabled
                    ? "Your clients can browse photos, watch the player, generate timelapse videos, and receive WhatsApp updates."
                    : "The link is disabled. Clients can't access this project."}
              </div>
            </div>
            <GatedControl locked={!sharingAllowed} tier={sharingTier} feature="Sharing">
              <Switch
                checked={enabled && sharingAllowed}
                disabled={!sharingAllowed}
                onChange={setEnabled}
              />
            </GatedControl>
          </div>

          {/* Share link (only if share exists) */}
          {share && (
            <div>
              <SectionLabel>Public link</SectionLabel>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "6px 6px 6px 12px",
                  background: "#fff",
                  border: BORDER,
                  borderRadius: RADIUS,
                  opacity: enabled ? 1 : 0.5,
                  transition: "opacity 180ms ease",
                }}
              >
                <MaterialIcon
                  name="link"
                  size={18}
                  style={{ color: SECONDARY, flexShrink: 0 }}
                />
                <span
                  onMouseDown={(e) => {
                    linkClickStartRef.current = { x: e.clientX, y: e.clientY };
                  }}
                  onMouseUp={async (e) => {
                    const start = linkClickStartRef.current;
                    linkClickStartRef.current = null;
                    if (!start) return;
                    const dx = Math.abs(e.clientX - start.x);
                    const dy = Math.abs(e.clientY - start.y);
                    // If the user dragged, treat it as a text selection: don't
                    // copy. Otherwise it's a click → copy to clipboard.
                    if (dx > 4 || dy > 4) return;
                    const url = `${window.location.origin}/s/${share.shareToken}`;
                    if (await copyToClipboard(url)) {
                      message.success("Link copied to clipboard");
                    } else {
                      message.error("Failed to copy link");
                    }
                  }}
                  style={{
                    flex: 1,
                    fontSize: 13,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    color: "#262626",
                    cursor: "pointer",
                    userSelect: "text",
                  }}
                  title="Click to copy"
                >
                  {`${window.location.origin}/s/${share.shareToken}`}
                </span>
                <Button
                  type="default"
                  size="small"
                  icon={<MaterialIcon name="content_copy" size={14} />}
                  onClick={handleCopyLink}
                >
                  Copy
                </Button>
              </div>
            </div>
          )}

          {/* Logo upload */}
          <div>
            <SectionLabel>Logo (white label)</SectionLabel>
            {logoUrl ? (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: 10,
                  border: BORDER,
                  borderRadius: RADIUS,
                  background: "#fff",
                }}
              >
                <div
                  style={{
                    width: 80,
                    height: 56,
                    borderRadius: 6,
                    background: "#fafafa",
                    border: "1px solid #f0f0f0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                  }}
                >
                  <img
                    src={logoUrl}
                    alt="Logo"
                    style={{
                      maxHeight: 48,
                      maxWidth: 72,
                      objectFit: "contain",
                    }}
                  />
                </div>
                <div style={{ flex: 1, fontSize: 13, color: SECONDARY }}>
                  Shown on the shared page header.
                </div>
                <Button
                  size="small"
                  icon={<MaterialIcon name="delete" size={14} />}
                  onClick={handleLogoRemove}
                >
                  Remove
                </Button>
              </div>
            ) : (
              <Upload.Dragger
                accept="image/*"
                showUploadList={false}
                multiple={false}
                beforeUpload={(file) => {
                  handleLogoUpload(file);
                  return false;
                }}
                style={{
                  padding: 0,
                  background: "transparent",
                  border: "none",
                }}
              >
                <div
                  className="share-logo-dropzone"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 10,
                    padding: "18px 16px",
                    border: `1px dashed ${uploadingLogo ? "#1677ff" : "#d9d9d9"}`,
                    borderRadius: RADIUS,
                    background: "#fafafa",
                    cursor: "pointer",
                    transition: "border-color 180ms ease, background 180ms ease",
                  }}
                >
                  <MaterialIcon
                    name={uploadingLogo ? "hourglass_empty" : "upload"}
                    size={18}
                    style={{ color: SECONDARY }}
                  />
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 500 }}>
                      {uploadingLogo ? "Uploading…" : "Upload your logo"}
                    </div>
                    <div style={{ fontSize: 12, color: SECONDARY }}>
                      PNG or JPG, shown on the shared page header.
                    </div>
                  </div>
                </div>
              </Upload.Dragger>
            )}
          </div>

          {/* Content modes — hard-lock: at least one must stay ON */}
          <div>
            <SectionLabel>Content shown to clients</SectionLabel>
            <div
              style={{
                border: BORDER,
                borderRadius: RADIUS,
                overflow: "hidden",
                background: "#fff",
              }}
            >
              {[
                {
                  key: "gallery" as const,
                  checked: galleryEnabled,
                  setter: setGalleryEnabled,
                  other: videoEnabled,
                  otherLabel: "Video",
                  title: "Gallery & player",
                  desc: "Clients can browse photos and use the player.",
                },
                {
                  key: "video" as const,
                  checked: videoEnabled,
                  setter: setVideoEnabled,
                  other: galleryEnabled,
                  otherLabel: "Gallery",
                  title: "Video generation",
                  desc: "Clients can generate and download timelapse videos.",
                },
              ].map((item, idx) => {
                const isLastActive = item.checked && !item.other;
                return (
                  <div
                    key={item.key}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      padding: "12px 14px",
                      borderTop: idx === 0 ? "none" : "1px solid #f0f0f0",
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 500, color: "#262626" }}>
                        {item.title}
                      </div>
                      <div style={{ fontSize: 12, color: SECONDARY, marginTop: 2 }}>
                        {item.desc}
                      </div>
                    </div>
                    <Tooltip
                      title={
                        isLastActive
                          ? `At least one mode must stay active. Enable ${item.otherLabel} to disable this.`
                          : ""
                      }
                    >
                      <span>
                        <Switch
                          checked={item.checked}
                          disabled={isLastActive}
                          onChange={item.setter}
                        />
                      </span>
                    </Tooltip>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Views selection */}
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "baseline",
                justifyContent: "space-between",
                marginBottom: 10,
              }}
            >
              <SectionLabel style={{ marginBottom: 0 }}>Shared views</SectionLabel>
              <Text style={{ fontSize: 12, color: SECONDARY }}>
                {sharedCount} of {views.length} shared
              </Text>
            </div>
            <div
              style={{
                border: BORDER,
                borderRadius: RADIUS,
                overflow: "hidden",
                background: "#fff",
              }}
            >
              {orderedViews.map((view, idx) => {
                const shared = !excludedViewIds.includes(view.viewId);
                const isDragOver = dragOverViewId === view.viewId && draggedViewId !== view.viewId;
                const isDragging = draggedViewId === view.viewId;
                return (
                  <div
                    key={view.viewId}
                    draggable
                    onDragStart={(e) => {
                      setDraggedViewId(view.viewId);
                      e.dataTransfer.effectAllowed = "move";
                      e.dataTransfer.setData("text/plain", view.viewId);
                    }}
                    onDragEnd={() => {
                      setDraggedViewId(null);
                      setDragOverViewId(null);
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.dataTransfer.dropEffect = "move";
                      if (dragOverViewId !== view.viewId) {
                        setDragOverViewId(view.viewId);
                      }
                    }}
                    onDragLeave={(e) => {
                      // Only clear if leaving the row itself (not entering a child)
                      if (e.currentTarget === e.target) {
                        setDragOverViewId((id) => (id === view.viewId ? null : id));
                      }
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      const sourceId = e.dataTransfer.getData("text/plain") || draggedViewId;
                      if (sourceId) reorderViews(sourceId, view.viewId);
                      setDraggedViewId(null);
                      setDragOverViewId(null);
                    }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      padding: "10px 14px",
                      borderTop: idx === 0 ? "none" : "1px solid #f0f0f0",
                      transition: "background 120ms ease",
                      background: isDragOver ? "#e6f4ff" : "#fff",
                      opacity: isDragging ? 0.4 : 1,
                    }}
                  >
                    <span
                      title="Drag to reorder"
                      style={{
                        cursor: "grab",
                        display: "flex",
                        alignItems: "center",
                        color: SECONDARY,
                        userSelect: "none",
                      }}
                    >
                      <MaterialIcon name="drag_indicator" size={18} />
                    </span>
                    <Checkbox
                      id={`share-view-${view.viewId}`}
                      checked={shared}
                      onChange={() => toggleViewExclusion(view.viewId)}
                    />
                    <label
                      htmlFor={`share-view-${view.viewId}`}
                      style={{ flex: 1, minWidth: 0, cursor: "pointer" }}
                    >
                      <div
                        style={{
                          fontSize: 14,
                          fontWeight: 500,
                          color: shared ? "#262626" : SECONDARY,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {view.name}
                      </div>
                    </label>
                    <Text style={{ fontSize: 12, color: SECONDARY }}>
                      {(view.stats?.photoCount ?? 0).toLocaleString()} photos
                    </Text>
                  </div>
                );
              })}
            </div>
            {!allViewsShared && sharedCount === 0 && (
              <Text
                type="danger"
                style={{ fontSize: 12, display: "block", marginTop: 6 }}
              >
                At least one view must be shared.
              </Text>
            )}
          </div>

          {/* Video settings */}
          <div>
            <SectionLabel>Video settings</SectionLabel>
            <Text style={{ fontSize: 12, color: SECONDARY, display: "block", marginBottom: 10 }}>
              Your clients can generate and download timelapse videos on their own from the shared page.
            </Text>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <div>
                <FieldLabel>Resolution</FieldLabel>
                <Select
                  value={resolution}
                  onChange={setResolution}
                  size="large"
                  style={{ width: "100%" }}
                  suffixIcon={<MaterialIcon name="expand_more" size={18} />}
                  options={[
                    { value: "1080p", label: "1080p" },
                    {
                      value: "4k",
                      label: maxResolution !== "4k" ? "4K — Pro" : "4K",
                      disabled: maxResolution !== "4k",
                    },
                  ]}
                />
              </div>
              <div>
                <FieldLabel>FPS</FieldLabel>
                <Select
                  value={fps}
                  onChange={setFps}
                  size="large"
                  style={{ width: "100%" }}
                  suffixIcon={<MaterialIcon name="expand_more" size={18} />}
                  options={FPS_OPTIONS.map((f) => ({ value: f, label: `${f} fps` }))}
                />
              </div>
              <div>
                <FieldLabel>Aspect ratio</FieldLabel>
                <Select
                  value={aspectRatio}
                  onChange={setAspectRatio}
                  size="large"
                  style={{ width: "100%" }}
                  suffixIcon={<MaterialIcon name="expand_more" size={18} />}
                  options={ASPECT_RATIO_OPTIONS.map((r) => ({ value: r, label: r }))}
                />
              </div>
            </div>
          </div>

          {/* Allowed timespans */}
          <div>
            <Text style={{ fontSize: 12, color: SECONDARY, display: "block", marginBottom: 10 }}>
              Choose which time periods your clients can select when generating videos.
            </Text>
            <Tag.CheckableTagGroup
              multiple
              options={ALL_TIMESPANS.map(({ value, label }) => ({ value, label }))}
              value={allowedTimespans}
              onChange={(vals) => {
                const next = vals as ShareTimespan[];
                if (next.length === 0) {
                  message.warning("Select at least one time range");
                  return;
                }
                setAllowedTimespans(next);
              }}
              style={{ display: "flex", flexWrap: "wrap", gap: 8 }}
            />
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 8,
                marginTop: 18,
                padding: "8px 10px",
                background: "#fffbe6",
                border: "1px solid #ffe58f",
                borderRadius: RADIUS,
              }}
            >
              <MaterialIcon
                name="warning"
                size={15}
                style={{ color: "#d48806", marginTop: 1, flexShrink: 0 }}
              />
              <Text style={{ fontSize: 12, color: "#874d00" }}>
                Videos generated by your clients consume your rendering credits.
              </Text>
            </div>
          </div>

          {/* WhatsApp daily — part of sharing, so it follows the same
              entitlement. The section stays visible when locked: a panel that
              vanishes tells the user nothing. */}
          <div>
              <SectionLabel>WhatsApp photo update</SectionLabel>
              <div
                style={{
                  border: BORDER,
                  borderRadius: RADIUS,
                  overflow: "hidden",
                  background: "#fff",
                }}
              >
                {/* Enable toggle */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "12px 14px",
                    background: waEnabled ? "#f0faf0" : undefined,
                    transition: "background 180ms ease",
                  }}
                >
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 500 }}>
                      Send latest photos via WhatsApp
                    </div>
                    <div style={{ fontSize: 12, color: SECONDARY, marginTop: 2 }}>
                      Each shared view sends its latest photo to your clients daily.
                    </div>
                  </div>
                  <GatedControl locked={!sharingAllowed} tier={sharingTier} feature="WhatsApp photo updates">
                    <Switch
                      checked={waEnabled && sharingAllowed}
                      disabled={!sharingAllowed}
                      onChange={setWaEnabled}
                    />
                  </GatedControl>
                </div>

                {/* Config fields (visible when enabled) */}
                {waEnabled && sharingAllowed && (
                  <div
                    style={{
                      padding: "14px",
                      borderTop: "1px solid #f0f0f0",
                      display: "flex",
                      flexDirection: "column",
                      gap: 16,
                    }}
                  >
                    {/* Recipients */}
                    <div>
                      <FieldLabel>Recipients</FieldLabel>
                      <WhatsAppRecipients
                        recipients={waRecipients}
                        optedOut={waOptedOut}
                        onChange={setWaRecipients}
                      />
                    </div>

                    {/* Frequency + Send time + timezone */}
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                      <div>
                        <FieldLabel>Every</FieldLabel>
                        <InputNumber
                          value={waIntervalDays}
                          min={1}
                          max={90}
                          onChange={(val) => setWaIntervalDays(val ?? 1)}
                          size="large"
                          style={{ width: "100%" }}
                          addonAfter={waIntervalDays === 1 ? "day" : "days"}
                        />
                      </div>
                      <div>
                        <FieldLabel>At</FieldLabel>
                        <TimePicker
                          value={dayjs(waSendTime, "HH:mm")}
                          format="HH:mm"
                          minuteStep={15}
                          onChange={(time) => {
                            if (time) setWaSendTime(time.format("HH:mm"));
                          }}
                          style={{ width: "100%" }}
                          size="large"
                          allowClear={false}
                        />
                      </div>
                      <div>
                        <FieldLabel>
                          Timezone{" "}
                          {waTimezoneSource === "view" && (
                            <Tag
                              color="blue"
                              style={{ fontSize: 10, lineHeight: "16px", marginLeft: 4, verticalAlign: "middle" }}
                            >
                              from device
                            </Tag>
                          )}
                        </FieldLabel>
                        <Select
                          value={waTimezone}
                          onChange={(val) => {
                            setWaTimezone(val);
                            setWaTimezoneSource("manual");
                          }}
                          size="large"
                          style={{ width: "100%" }}
                          showSearch
                          filterOption={(input, option) =>
                            String(option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                          }
                          options={COMMON_TIMEZONES.map((tz) => ({
                            value: tz,
                            label: tz.replace(/_/g, " "),
                          }))}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
          </div>

        </div>
      )}
    </Modal>
  );
}
